import { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";

/**
 * usePathTab keeps the selected tab in sync with a URL path segment instead
 * of a query string. Pair it with a `:tabParam` placeholder in the route :
 *
 *     // router :
 *     <Route path="forums" element={<ForumsPage />} />
 *     <Route path="forums/:cat" element={<ForumsPage />} />
 *
 *     // component :
 *     const [tab, setTab] = usePathTab("cat", "/forums", "all", ["all", "hot", ...]);
 *
 * - `param`   : name of the route param (must match the placeholder).
 * - `basePath`: the URL prefix we navigate against (e.g. "/forums").
 * - `def`     : default value when the path doesn't specify one.
 * - `allowed` : whitelist ; unknown values fall back to `def`.
 *
 * `setValue` uses `replace` so flipping tabs doesn't spam the browser history.
 * The default value yields the base path (no trailing segment).
 */
export function usePathTab<T extends string>(
  param: string,
  basePath: string,
  def: T,
  allowed?: readonly T[]
): [T, (next: T) => void] {
  const params = useParams();
  const navigate = useNavigate();
  const raw = params[param] ?? "";

  const isValid = (v: string): v is T =>
    !allowed ? v.length > 0 : (allowed as readonly string[]).includes(v);

  const value = isValid(raw) ? (raw as T) : def;

  const setValue = useCallback(
    (next: T) => {
      const base = basePath.replace(/\/+$/, "");
      const target = next === def ? base : `${base}/${encodeURIComponent(next)}`;
      navigate(target, { replace: true });
    },
    [navigate, basePath, def]
  );

  return [value, setValue];
}
